import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import CallResults from '../../components/CallResults/CallResults';
import storageManager from '../../utils/storageManager';
import CandidateCard from './CandidateCard';
import ContactHistorySection from './ContactHistorySection';
import CVExpandedSection from './CVExpandedSection';
import EvaluationModal from './EvaluationModal';
import RemoveReasonModal from './RemoveReasonModal';
import ResultsHeader from './ResultsHeader';

/**
 * Modern Results Section Component
 * Combines ResultsSection.js functionality with modern Metronic design
 * Based on conversation-mockup.html
 */
function ModernResultsSection({
  jobId,
  jobTitle,
  results,
  onCallInitiate,
  showCallStatuses,
  callResults,
  onCallResultsUpdate,
  getTranscript,
  emailSummary,
  onCandidateRemove,
  removedCandidates,
  removalStats,
  setMessages,
  setCurrentStep
}) {
  const [isCallResultsVisible, setIsCallResultsVisible] = useState(false);
  const [processedResults, setProcessedResults] = useState([]);
  const [effectiveJobId, setEffectiveJobId] = useState(jobId);
  const [flowState, setFlowState] = useState('initial');
  const [expandedResults, setExpandedResults] = useState(new Set());
  const [selectedCandidates, setSelectedCandidates] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Modal states
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [candidateToRemove, setCandidateToRemove] = useState(null);
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [selectedEvaluationCandidate, setSelectedEvaluationCandidate] = useState(null);

  // Helper function for display name
  const getDisplayName = (candidate) => {
    if (candidate.name && candidate.name !== "Unknown") {
      return candidate.name;
    }
    const fileName = candidate.id?.split('/').pop() || '';
    return fileName.replace(/\.[^/.]+$/, "").replace(/[_-]/g, " ") || "Unnamed Candidate";
  };

  // Error handler
  const handleError = React.useCallback((error, context) => {
    console.error(`Error in ${context}:`, error);
    setMessages(prevMessages => [
      ...prevMessages,
      { text: `Error: ${error.response?.data?.message || error.message}`, sender: 'system' }
    ]);
  }, [setMessages]);

  // Process results
  useEffect(() => {
    try {
      const storedJobId = localStorage.getItem('currentJobId');
      const newEffectiveJobId = jobId || storedJobId;

      setEffectiveJobId(newEffectiveJobId);

      if (results && Array.isArray(results)) {
        if (newEffectiveJobId) {
          storageManager.storeResults(newEffectiveJobId, results);
        }

        // Filter out removed candidates
        const filteredResults = results.filter(result => !removedCandidates?.has(result.id));

        console.log('ModernResultsSection - Filtering results:');
        console.log('Original results count:', results.length);
        console.log('Removed candidates count:', removedCandidates?.size || 0);
        console.log('Filtered results count:', filteredResults.length);

        const processed = filteredResults.map(result => {
          console.log('Processing result ID:', result.id);

          // Create a basic contact history object with defaults
          let processedContactHistory = {
            contacted: false,
            lastContactDate: null,
            lastContactMethod: null,
            lastJobId: null,
            lastJobTitle: null
          };

          // Process contact history (same logic as original)
          if (result.contactHistory) {
            if (result.contactHistory.contacted !== undefined) {
              processedContactHistory.contacted = Boolean(result.contactHistory.contacted);
            }

            if (result.contactHistory.lastContactDate) {
              processedContactHistory.contacted = true;
              processedContactHistory.lastContactDate = result.contactHistory.lastContactDate;
            }

            if (result.contactHistory.lastContactMethod) {
              processedContactHistory.lastContactMethod = result.contactHistory.lastContactMethod;
            }

            if (result.contactHistory.lastJobId) {
              processedContactHistory.lastJobId = result.contactHistory.lastJobId;
            }

            if (result.contactHistory.lastJobTitle) {
              processedContactHistory.lastJobTitle = result.contactHistory.lastJobTitle;
            }

            // Process raw_communication_data
            if (result.contactHistory.raw_communication_data) {
              const rawComm = result.contactHistory.raw_communication_data;
              processedContactHistory.contacted = true;

              if (rawComm.stats && rawComm.stats.lastContactDate) {
                processedContactHistory.lastContactDate = rawComm.stats.lastContactDate;
              }

              if (rawComm.calls && rawComm.calls.history && rawComm.calls.history.length > 0) {
                processedContactHistory.contacted = true;
                const sortedCalls = [...rawComm.calls.history].sort((a, b) =>
                  new Date(b.timestamp) - new Date(a.timestamp));
                const latestCall = sortedCalls[0];

                if (latestCall.jobId && !processedContactHistory.lastJobId) {
                  processedContactHistory.lastJobId = latestCall.jobId;
                }

                if (latestCall.timestamp && !processedContactHistory.lastContactDate) {
                  processedContactHistory.lastContactDate = latestCall.timestamp;
                }
              }

              if (rawComm.email && rawComm.email.history && rawComm.email.history.length > 0) {
                processedContactHistory.contacted = true;
                if (!processedContactHistory.lastContactDate && rawComm.email.lastContactDate) {
                  processedContactHistory.lastContactDate = rawComm.email.lastContactDate;
                }
              }
            }
          }

          return {
            ...result,
            id: result.id || `temp-${Math.random()}`,
            contactName: getDisplayName(result),
            score: result.score || result.combined_score || 0,
            similarity: result.similarity || 0,
            keyword_matches: result.keyword_matches || result.keyword_match_count || 0,
            rating: result.rating || null,
            jobId: newEffectiveJobId,
            contactHistory: processedContactHistory
          };
        });

        console.log('Processed results:', processed);
        setProcessedResults(processed);
      }
    } catch (error) {
      handleError(error, 'processing results');
    }
  }, [results, jobId, jobTitle, handleError, removedCandidates]);

  // Call results visibility
  useEffect(() => {
    if (showCallStatuses && callResults?.length > 0) {
      setIsCallResultsVisible(true);
    } else {
      setIsCallResultsVisible(false);
    }
  }, [showCallStatuses, callResults]);

  // Flow state management
  const isEvaluationResults = results && results[0] && results[0].hasOwnProperty('rating');

  useEffect(() => {
    if (!jobId && !results) {
      setFlowState('awaiting_job_description');
    } else if (jobId && !results) {
      setFlowState('awaiting_query');
    } else if (jobId && results && !isEvaluationResults) {
      setFlowState('query_complete');
    } else if (isEvaluationResults) {
      setFlowState('evaluation_complete');
    }
  }, [jobId, results, isEvaluationResults]);

  // Remove handlers
  const handleShowRemoveModal = (candidateId) => {
    const candidate = processedResults.find(r => r.id === candidateId);
    setCandidateToRemove(candidate);
    setShowRemoveModal(true);
  };

  const handleConfirmRemoval = (reason) => {
    if (!candidateToRemove || !onCandidateRemove) return;

    onCandidateRemove(candidateToRemove.id, reason, candidateToRemove);
    setShowRemoveModal(false);
    setCandidateToRemove(null);
  };

  // View evaluation handler
  const handleViewEvaluation = (candidate) => {
    setSelectedEvaluationCandidate(candidate);
    setShowEvaluationModal(true);
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedCandidates(new Set(processedResults.map(result => result.id)));
    } else {
      setSelectedCandidates(new Set());
    }
    setSelectAll(checked);
  };

  const handleSelectCandidate = (candidateId) => {
    setSelectedCandidates(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(candidateId)) {
        newSelected.delete(candidateId);
      } else {
        newSelected.add(candidateId);
      }
      setSelectAll(newSelected.size === processedResults.length && processedResults.length > 0);
      return newSelected;
    });
  };

  // Toggle CV expansion
  const toggleExpand = (id) => {
    setExpandedResults((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };


  // Flow state renders
  if (flowState === 'awaiting_job_description') {
    return (
      <div className="flex items-center justify-center h-64 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="text-center text-gray-500">
          <p className="text-lg">Please upload a job description to begin.</p>
        </div>
      </div>
    );
  }

  if (flowState === 'awaiting_query') {
    return (
      <div className="flex items-center justify-center h-64 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="text-center text-gray-500">
          <p className="text-lg">Job Description uploaded. Please enter your search query.</p>
        </div>
      </div>
    );
  }

  if (!results || results.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="text-center text-gray-500">
          <p className="text-lg">No results found {jobTitle ? `for job: ${jobTitle}` : ''}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Results Header */}
      <ResultsHeader
        jobTitle={isEvaluationResults ? `Evaluation Results for: ${jobTitle || 'Job Position'}` : `Search Results for: ${jobTitle || 'Job Position'}`}
        totalCount={results.length}
        showingCount={processedResults.length}
        selectAll={selectAll}
        onSelectAllChange={handleSelectAll}
        selectedCount={selectedCandidates.size}
        onBulkRemove={() => {
          // Bulk remove functionality
          if (selectedCandidates.size === 0) return;
          const reason = prompt('Please enter a reason for removing these candidates:');
          if (!reason) return;

          selectedCandidates.forEach(candidateId => {
            const candidate = processedResults.find(r => r.id === candidateId);
            if (candidate && onCandidateRemove) {
              onCandidateRemove(candidateId, reason, candidate);
            }
          });

          setSelectedCandidates(new Set());
          setSelectAll(false);
        }}
      />

      {/* Candidate Cards */}
      <div className="bg-white rounded-b-lg shadow-sm border border-t-0 border-gray-200">
        <div className="p-6 space-y-4 max-h-[600px] overflow-y-auto custom-scrollbar">
          {processedResults && processedResults.length > 0 ? (
            processedResults.map((candidate) => (
              <CandidateCard
                key={candidate.id}
                candidate={candidate}
                selected={selectedCandidates.has(candidate.id)}
                onSelect={() => handleSelectCandidate(candidate.id)}
                onRemove={handleShowRemoveModal}
                onViewEvaluation={handleViewEvaluation}
                isEvaluationResult={isEvaluationResults}
                expanded={expandedResults.has(candidate.id)}
                onToggleExpand={() => toggleExpand(candidate.id)}
                expandedContent={
                  <>
                    <CVExpandedSection
                      candidate={candidate}
                      onCollapse={() => toggleExpand(candidate.id)}
                      showQuickActions={true}
                    />
                    {candidate.contactHistory?.contacted && (
                      <div className="mt-4">
                        <ContactHistorySection contactHistory={candidate.contactHistory} />
                      </div>
                    )}
                  </>
                }
              />
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No results available {jobTitle ? `for job: ${jobTitle}` : ''}</p>
            </div>
          )}
        </div>
      </div>

      {/* Email Summary - Keep original styling for now */}
      {emailSummary && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{emailSummary.successful_emails}</div>
              <div className="text-sm text-gray-600">Successfully Sent</div>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{emailSummary.failed_emails}</div>
              <div className="text-sm text-gray-600">Failed</div>
            </div>
            {emailSummary.skipped !== undefined && (
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{emailSummary.skipped}</div>
                <div className="text-sm text-gray-600">Skipped</div>
              </div>
            )}
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {emailSummary.total_processed || (emailSummary.successful_emails + emailSummary.failed_emails + (emailSummary.skipped || 0))}
              </div>
              <div className="text-sm text-gray-600">Total Processed</div>
            </div>
          </div>

          {/* Email tracking details */}
          {emailSummary.tracking_details && emailSummary.tracking_details.length > 0 && (
            <div className="mt-4">
              <h4 className="font-semibold text-gray-900 mb-2">Email Status Details</h4>
              <div className="space-y-2">
                {emailSummary.tracking_details.map((track, index) => (
                  <div
                    key={`tracking-${index}`}
                    className={`p-3 rounded-lg border ${
                      track.status.includes('SUCCESS') ? 'bg-green-50 border-green-200' :
                      track.status.includes('FAILED') ? 'bg-red-50 border-red-200' :
                      'bg-yellow-50 border-yellow-200'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-medium text-gray-900">{track.candidate}</span>
                        <span className="text-sm text-gray-600 ml-2">{track.email}</span>
                      </div>
                      <span className="text-xs font-medium">{track.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Removal Stats - Keep original styling for now */}
      {removalStats && removalStats.totalRemoved > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Removal Statistics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{removalStats.totalRemoved}</div>
              <div className="text-sm text-gray-600">Total Removed</div>
            </div>
          </div>

          {/* Removal reasons */}
          {Object.keys(removalStats.reasonCounts).length > 0 && (
            <div className="mt-4">
              <h4 className="font-semibold text-gray-900 mb-2">Removal Reasons:</h4>
              <div className="space-y-2">
                {Object.entries(removalStats.reasonCounts).map(([reason, count]) => (
                  <div key={reason} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="text-sm text-gray-700">{reason}</span>
                    <span className="text-sm font-medium text-gray-900">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Most removed skills */}
          {Object.keys(removalStats.removedSkills || {}).length > 0 && (
            <div className="mt-4">
              <h4 className="font-semibold text-gray-900 mb-2">Most Removed Skills:</h4>
              <div className="flex flex-wrap gap-2">
                {Object.entries(removalStats.removedSkills)
                  .sort(([,a], [,b]) => b - a)
                  .slice(0, 5)
                  .map(([skill, count]) => (
                    <span key={skill} className="px-3 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                      {skill} ({count})
                    </span>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Call Results Section */}
      {isCallResultsVisible && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Call Results</h3>
          <CallResults
            callResults={callResults}
            getTranscript={getTranscript}
            showCallStatuses={true}
            onUpdateCallResults={onCallResultsUpdate}
          />
        </div>
      )}

      {/* Modals */}
      <RemoveReasonModal
        isOpen={showRemoveModal}
        onClose={() => {
          setShowRemoveModal(false);
          setCandidateToRemove(null);
        }}
        onConfirm={handleConfirmRemoval}
        candidate={candidateToRemove}
      />

      <EvaluationModal
        isOpen={showEvaluationModal}
        onClose={() => {
          setShowEvaluationModal(false);
          setSelectedEvaluationCandidate(null);
        }}
        candidate={selectedEvaluationCandidate}
        jobTitle={jobTitle}
        jobId={effectiveJobId}
      />
    </div>
  );
}

ModernResultsSection.propTypes = {
  jobId: PropTypes.string,
  jobTitle: PropTypes.string,
  results: PropTypes.array,
  onCallInitiate: PropTypes.func,
  showCallStatuses: PropTypes.bool,
  callResults: PropTypes.array,
  onCallResultsUpdate: PropTypes.func,
  getTranscript: PropTypes.func,
  emailSummary: PropTypes.object,
  onCandidateRemove: PropTypes.func,
  removedCandidates: PropTypes.instanceOf(Set),
  removalStats: PropTypes.object,
  setMessages: PropTypes.func,
  setCurrentStep: PropTypes.func
};

export default ModernResultsSection;

